import type { Middleware } from 'redux';
import type { SupportRequestUserAction } from 'types';
import { toPathOnly } from './requestPath';

const MAX_USER_ACTIONS = 20;
const MAX_TYPE_LENGTH = 100;
const MAX_TARGET_LENGTH = 500;

const userActions: SupportRequestUserAction[] = [];

/** Record one action. Oldest entry is evicted once the buffer is full. */
export function recordUserAction(type: string, target: string): void {
  userActions.push({
    at: new Date().toISOString(),
    type: type.slice(0, MAX_TYPE_LENGTH),
    target: target.slice(0, MAX_TARGET_LENGTH),
  });

  if (userActions.length > MAX_USER_ACTIONS) {
    userActions.shift();
  }
}

/** The recorded actions, oldest first, as the contract expects them. */
export function getUserActions(): SupportRequestUserAction[] {
  return [...userActions];
}

/** Test-only: the buffer outlives any single test otherwise. */
export function resetUserActions(): void {
  userActions.length = 0;
}

/**
 * Redux middleware that logs the type of every action dispatched. The target is
 * the slice the action belongs to (`user/setUser` -> `user`), which is what a
 * reader of the support email can actually orient by.
 *
 * Redux's own lifecycle actions (`@@redux/INIT`, `@@INIT`) are skipped: they are
 * store setup, not something the user did.
 */
export const userActionLogMiddleware: Middleware = () => (next) => (action) => {
  const { type } = action ?? {};

  if (typeof type === 'string' && !type.startsWith('@@')) {
    recordUserAction(type, type.split('/')[0]);
  }

  return next(action);
};

/**
 * Log a completed client-side navigation. The router reports `asPath`, so the
 * query string is dropped before storing — it carries record ids and filter
 * values that have no business in an outbound email.
 */
export function recordNavigation(url: string): void {
  recordUserAction('navigate', toPathOnly(url));
}
